/**
 * space_stars — the catalogue's stars as seen from a VACUUM (report §9 R2): the ground's analytic point stars
 * (lib/sky/star_field.ts — the one-cell-per-pixel lookup, the energy-conserving Gaussian core + halo, the
 * no-popping reach window, the per-star colour) with three changes the ground cannot have:
 *   1. NO scintillation (no air) — the two-sine twinkle term is gone entirely, not weighted to zero.
 *   2. A MAGNITUDE STRETCH: radiance = SPACE_STAR_P99_TARGET · (peak / p99)^γ. The photo-calibrated fluxes put
 *      the median star at ~1/20 of the 99th percentile; drawn linearly (gain chosen so the top 1 % blooms) the
 *      tens of thousands of faint stars all landed at a visible mid-grey and the void read as noise. γ = 1.8
 *      keeps the top ~1 % over the bloom threshold and sends the faint majority toward black — a
 *      short-exposure sky: a few hundred clear stars, the Milky Way as texture, deep blacks.
 *   3. The caller rotates the lookup direction per system (space_sky).
 * The stretch rule is space_look.spaceStarRadiance (pinned by verify:space-look).
 */
import {
  Fn, float, int, vec3, ivec2, min, max, exp, length, fwidth, smoothstep, textureLoad, Loop, pow, clamp, floor, select,
} from 'three/tsl';
import { cubeCellNode, type StarFieldInput } from '../sky/star_field.js';
import { STAR_SHRINK, STAR_HALO } from '../sky/star_catalog.js';
import { SPACE_STAR_GAMMA_HI } from './space_look.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

/** Unpack star_catalog's `packColor` (r·65536 + g·256 + b, each ×100). */
const unpackColor = (p: N): N => {
  const r = floor(p.div(65536));
  const rest = p.sub(r.mul(65536));
  const g = floor(rest.div(256));
  const b = rest.sub(g.mul(256));
  return vec3(r, g, b).mul(0.01);
};

/**
 * The vacuum star field along `dir` (unit, the system's sky frame). `invP99` = 1 / the catalogue's 99th-percentile
 * peak, `gamma` the stretch, `target` the radiance the p99 star lands at (uniform nodes or numbers).
 */
export function buildSpaceStarField(dir: N, inp: StarFieldInput, u: { invP99: N; gamma: N; target: N; floor0?: N }): N {
  const G = inp.grid, RW = inp.recordsWidth;
  return Fn(() => {
    const d = dir;
    const { face, i, j } = cubeCellNode(d, G);
    const cell = textureLoad(inp.cells, ivec2(face.mul(G).add(i), j));
    const start = cell.x, count = cell.y;
    const n = min(count, float(inp.maxPerCell)).toInt();
    // THE LAZY-VAR LAW: a derivative is emitted where it is first USED — here that was inside the per-star loop, whose
    // count is the cell's own (non-uniform control flow: undefined in GLSL and WGSL alike). Pinned above the loop.
    const dAA = clamp(length(fwidth(d)), 0.00035, 0.02).toVar();
    const pxFloor = dAA.mul(0.75).toVar();
    const acc = vec3(0).toVar();
    Loop({ start: int(0), end: n, type: 'int', condition: '<' }, ({ i: k }: { i: N }) => {
      const e = start.toInt().add(k).mul(2).toFloat();
      const ty = floor(e.div(RW));
      const tx = e.sub(ty.mul(RW));
      const t0 = textureLoad(inp.records, ivec2(tx, ty));
      const t1 = textureLoad(inp.records, ivec2(tx.add(1), ty));
      const sdir = t0.xyz, sigma = t0.w;
      const packed = t1.x, flux = t1.y, reach = t1.z;
      const dist = length(d.sub(sdir));
      // The PSF at the pixel floor (a faint star dims rather than shrinks — the anti-shimmer law) …
      const sigE = max(sigma.mul(STAR_SHRINK), pxFloor);
      // THE DISPLAY'S STRETCH, THE LENS'S SHAPE (lens_stars.ts): along a bent direction `dAA` is the LENSED beam, and
      // stretching a peak taken at it read a squeezed sky as dimmer stars (2^-3.6 per doubling). With `floor0` — the
      // floor the same pixel has with no hole — the stretch is the plain sky's, and the lensed floor only shapes the
      // Gaussian at the same flux. Absent (the plain sky), nothing changes.
      const sig0 = u.floor0 ? max(sigma.mul(STAR_SHRINK), u.floor0) : sigE;
      const peak = flux.div(sig0.mul(sig0).mul(2 * Math.PI));
      // … whose peak is STRETCHED (the magnitude stretch), the Gaussian shape kept.
      const x = peak.mul(u.invP99);
      const stretched = pow(x, select(x.lessThan(1), u.gamma, float(SPACE_STAR_GAMMA_HI))).mul(u.target);
      const core = exp(dist.mul(dist).div(sigE.mul(sigE).mul(-2)));
      const halo = exp(dist.div(sigma).negate()).mul(STAR_HALO);
      const win = float(1).sub(smoothstep(reach.mul(0.7), reach, dist));
      if (u.floor0) acc.addAssign(unpackColor(packed).mul(stretched.mul(core.mul(sig0.mul(sig0).div(sigE.mul(sigE))).add(halo)).mul(win)));
      else acc.addAssign(unpackColor(packed).mul(stretched.mul(core.add(halo)).mul(win)));
    });
    return acc;
  })();
}
